import naja from 'naja';

// Bezpečnostní pojistka proti zacyklení AJAX požadavků (např. showInlineAdd ⇄ inlineEdit
// na výdejce), které zahltí hlavní vlákno a prohlížeč zahlásí "Stránka nereaguje".
// Pouze detekuje, zastaví záplavu a nahlásí ji na server – příčinu neřeší.
const WINDOW_MS = 2000;     // klouzavé okno pro detekci rychlé záplavy
const THRESHOLD = 10;       // max requestů v okně, než to vyhodnotíme jako smyčku
// Druhá pojistka: pomalá smyčka stejného signálu (např. setStoreTo→showInlineAdd→400),
// která nikdy nedosáhne rychlého prahu, ale opakuje se donekonečna.
const SIGNAL_WINDOW_MS = 8000;  // delší okno pro opakování téhož signálu
const SIGNAL_THRESHOLD = 6;     // kolikrát se smí týž signál zopakovat, než to bereme jako smyčku
const COOLDOWN_MS = 10000;  // jak dlouho blokovat, než se hlídač resetuje
const BEACON_THROTTLE_MS = 60000; // max jeden report za minutu (ať nezahltíme log)
const BEACON_URL = '/main/js-guard/loop';

class LoopGuardExtension {
    initialize(naja) {
        this.timestamps = [];
        this.urls = [];
        this.signalHits = [];   // [{ signal, ts }] pro detekci pomalé smyčky téhož signálu
        this.tripped = false;
        this.lastBeaconAt = 0;

        naja.uiHandler.addEventListener('interaction', (event) => this.onInteraction(event));
        naja.addEventListener('before', (event) => this.onBefore(event));
    }

    // Request, který vyvolal skutečný klik nebo submit uživatele (isTrusted), si
    // označíme. Smyčka je kód, který se volá sám - člověk, který proklikává
    // týdny v rozpisu, pošle 7× plan-changeWeek za pár sekund zcela legitimně
    // a pojistka ho dřív na 10 s zablokovala (repeated-signal:plan-changeWeek
    // v js-loop.log, 5. 10. 2026). Programové clickElement() originalEvent nemá.
    onInteraction(event) {
        if (event.detail.originalEvent?.isTrusted) {
            event.detail.options.loopGuardUserAction = true;
        }
    }

    // Vytáhne z URL název Nette signálu (parametr `do=`), ať se různé grid-id téhož
    // signálu (…grid-grid-inlineEdit) počítají dohromady.
    extractSignal(url) {
        const m = /[?&]do=([^&]+)/.exec(url);
        return m ? decodeURIComponent(m[1]) : url;
    }

    onBefore(event) {
        const now = Date.now();

        const url = event.detail?.url || '';
        this.urls.push(url);
        if (this.urls.length > 50) this.urls.shift();

        this.timestamps.push(now);
        while (this.timestamps.length && now - this.timestamps[0] > WINDOW_MS) {
            this.timestamps.shift();
        }

        // Klouzavé okno pro opakování téhož signálu. Kliky uživatele se do něj
        // nepočítají (viz onInteraction), hlídá je jen rychlá záplava.
        const signal = this.extractSignal(url);
        const userAction = event.detail?.options?.loopGuardUserAction === true;
        if (!userAction) {
            this.signalHits.push({ signal, ts: now });
        }
        while (this.signalHits.length && now - this.signalHits[0].ts > SIGNAL_WINDOW_MS) {
            this.signalHits.shift();
        }

        // Po sepnutí blokujeme veškeré requesty až do resetu – tím se smyčka přetne.
        if (this.tripped) {
            event.preventDefault();
            return;
        }

        // 1) rychlá záplava jakýchkoli requestů
        if (this.timestamps.length > THRESHOLD) {
            this.trip(event, 'flood');
            return;
        }

        // 2) pomalá smyčka: týž signál se v delším okně opakuje nad limit.
        // Výjimka: autocomplete – při psaní delšího dotazu jde legitimně request
        // na každý úhoz (7+ za 8 s je běžné psaní), takže tady pojistka dřív
        // blokovala uživatele uprostřed psaní (viz repeated-signal:...autocomplete
        // v js-loop.log). Autocomplete se navíc sám deduplikuje (nový request
        // abortuje předchozí), takže smyčku tvořit nemůže; rychlá záplava (bod 1)
        // ho hlídá dál.
        if (!userAction && !/autocomplete/i.test(signal)) {
            const sameSignal = this.signalHits.filter(h => h.signal === signal).length;
            if (sameSignal > SIGNAL_THRESHOLD) {
                this.trip(event, 'repeated-signal:' + signal);
            }
        }
    }

    trip(event, reason = 'flood') {
        this.tripped = true;
        event.preventDefault();

        const report = {
            page: window.location.href,
            reason,
            count: this.timestamps.length,
            windowMs: WINDOW_MS,
            threshold: THRESHOLD,
            urls: this.urls.slice(-20),
            ts: new Date().toISOString(),
        };

        console.error('[LoopGuard] Zastaveno zacyklení AJAX požadavků – nahlášeno na server.', report);
        this.sendBeacon(report);

        // Po cooldownu hlídač resetujeme, aby aplikace zůstala použitelná.
        setTimeout(() => {
            this.tripped = false;
            this.timestamps = [];
            this.urls = [];
            this.signalHits = [];
        }, COOLDOWN_MS);
    }

    sendBeacon(report) {
        const now = Date.now();
        if (now - this.lastBeaconAt < BEACON_THROTTLE_MS) return;
        this.lastBeaconAt = now;

        try {
            const body = JSON.stringify(report);
            if (navigator.sendBeacon) {
                navigator.sendBeacon(BEACON_URL, new Blob([body], { type: 'application/json' }));
            } else {
                fetch(BEACON_URL, {
                    method: 'POST',
                    body,
                    headers: { 'Content-Type': 'application/json' },
                    keepalive: true,
                });
            }
        } catch (e) {
            // Logování nesmí nikdy shodit appku.
        }
    }
}

export default LoopGuardExtension;
